import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ChevronRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  ClockIcon,
  RadioIcon,
  SendIcon,
  ShoppingCartIcon,
  WebhookIcon,
  type LucideIcon,
} from "lucide-react";

import { Pill } from "~/components/status-badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { errorMessage } from "~/lib/api";
import { formatDateTime, formatDuration, formatRelative } from "~/lib/format";
import { useOrderTimeline, type TimelineEntry, type TimelineSource } from "~/lib/queries";
import { cn } from "~/lib/utils";

/**
 * A gap worth drawing. Anything shorter is normal machinery -- a webhook and the
 * status update it triggers are milliseconds apart.
 */
const GAP_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * One order's history from three tables, on one axis.
 *
 * The reason the two milestones before this one exist. An order's story lives in
 * webhook_events (what DurianPay told us), transactions (what we did) and
 * callback_delivery_attempts (what we told the merchant), and answering "why is
 * this payment stuck" has meant holding all three in your head in psql.
 *
 * Interleaved, the answer is usually visible in seconds -- and it is usually a
 * gap, which is why this component draws holes rather than letting two rows sit
 * flush against each other. A four-hour hole between "webhook received" and
 * "callback attempted" IS the finding; a timeline that renders it as two
 * adjacent entries hides exactly what you opened it to see.
 */
export function OrderTimeline({ orderId }: { orderId: string }) {
  const { data, error, isPending } = useOrderTimeline(orderId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Timeline</CardTitle>
        <CardDescription>
          Merged from webhook events, this order, and merchant callbacks.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : error ? (
          <p className="text-sm text-muted-foreground">{errorMessage(error)}</p>
        ) : !data || data.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing has happened to this order beyond its creation.
          </p>
        ) : (
          <>
            {/* The provider referenced an order we never created. Worth saying
                loudly on a page that is otherwise about an order that exists. */}
            {!data.transaction_found ? (
              <p className="mb-3 rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs text-muted-foreground">
                No transaction row exists for this order. Everything below came from webhooks
                and callbacks that referenced it.
              </p>
            ) : null}

            <ol className="space-y-0">
              {data.entries.map((entry, index) => (
                <Entry
                  key={`${entry.timestamp}-${entry.kind}-${index}`}
                  entry={entry}
                  previous={index > 0 ? data.entries[index - 1] : undefined}
                  last={index === data.entries.length - 1}
                />
              ))}
            </ol>

            {data.truncated ? (
              <p className="mt-3 text-xs text-muted-foreground">
                Only the first 500 entries are shown.
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Entry({
  entry,
  previous,
  last,
}: {
  entry: TimelineEntry;
  previous: TimelineEntry | undefined;
  last: boolean;
}) {
  const [open, setOpen] = useState(false);
  const gap = previous ? gapBetween(previous.timestamp, entry.timestamp) : 0;
  const Icon = iconFor(entry);
  const tone = TONES[entry.source];
  const expandable = entry.detail != null && Object.keys(entry.detail).length > 0;

  return (
    <li>
      {gap > GAP_THRESHOLD_MS ? <Gap ms={gap} /> : null}

      <div className="relative flex gap-3 pb-4">
        {/* The rail. Stops at the last entry so the timeline ends rather than
            trailing off into whitespace. */}
        {!last ? (
          <span
            aria-hidden
            className="absolute top-7 bottom-0 left-[0.6875rem] w-px bg-border"
          />
        ) : null}

        <span
          className={cn(
            "z-10 grid size-6 shrink-0 place-items-center rounded-full border",
            tone.dot,
          )}
        >
          <Icon className="size-3.5" />
        </span>

        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => expandable && setOpen((value) => !value)}
            aria-expanded={expandable ? open : undefined}
            className={cn(
              "group flex w-full items-start gap-1.5 text-left",
              expandable ? "cursor-pointer" : "cursor-default",
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm leading-snug">{entry.summary}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                <span title={formatDateTime(entry.timestamp)}>
                  {formatRelative(entry.timestamp)}
                </span>
                <span className="mx-1.5 opacity-40">·</span>
                {formatDateTime(entry.timestamp)}
              </span>
            </span>

            {/* Deduced, not recorded. There is no status-transition table, and
                presenting an inference as a logged fact is how someone
                concludes the wrong thing about when a payment changed state. */}
            {entry.inferred ? (
              <Pill
                tone="neutral"
                className="shrink-0 text-[0.6875rem]"
                title="Deduced from surrounding records, not a logged transition — Altura keeps no status history"
              >
                inferred
              </Pill>
            ) : null}

            {expandable ? (
              <ChevronRightIcon
                className={cn(
                  "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
                  open && "rotate-90",
                )}
              />
            ) : null}
          </button>

          {open && entry.detail ? (
            <dl className="mt-2 space-y-1 rounded-md border bg-muted/40 p-2.5 text-xs">
              {Object.entries(entry.detail).map(([key, value]) => (
                <div key={key} className="flex gap-2">
                  <dt className="shrink-0 font-medium text-muted-foreground">{key}</dt>
                  <dd className="min-w-0 flex-1 font-mono break-all">{render(value)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/**
 * A hole in the chronology, drawn as a hole.
 *
 * This is the component's whole reason for existing. The finding on a stuck
 * order is almost never an entry — it is the four hours between two of them.
 */
function Gap({ ms }: { ms: number }) {
  return (
    <div className="relative flex items-center gap-3 pb-4" aria-label={`Gap of ${formatDuration(ms)}`}>
      <span
        aria-hidden
        className="absolute top-0 bottom-0 left-[0.6875rem] w-px border-l border-dashed border-border"
      />
      <span className="z-10 size-6 shrink-0" />
      <span className="text-xs text-muted-foreground italic">
        {formatDuration(ms)} later
      </span>
    </div>
  );
}

/**
 * Colour by source, on the same semantic tokens as every badge in the app.
 *
 * Source, not outcome: the point of the coding is to let the eye separate "what
 * they told us" from "what we told them" at a glance, which is the separation
 * the whole screen is built to make. A failure still reads as a failure from its
 * icon and its summary.
 */
const TONES: Record<TimelineSource, { dot: string }> = {
  webhook: { dot: "border-info/30 bg-info/12 text-info" },
  transaction: { dot: "border-border bg-muted text-muted-foreground" },
  callback: { dot: "border-success/30 bg-success/12 text-success" },
};

/** An icon per kind, falling back to the source's own. */
const ICONS: Record<string, LucideIcon> = {
  order_created: ShoppingCartIcon,
  order_updated: ShoppingCartIcon,
  order_expiry: ClockIcon,
  webhook_received: WebhookIcon,
  webhook_processed: CircleCheckIcon,
  webhook_failed: CircleAlertIcon,
  callback_enqueued: RadioIcon,
  callback_attempt: SendIcon,
  callback_delivered: CircleCheckIcon,
};

function iconFor(entry: TimelineEntry): LucideIcon {
  return ICONS[entry.kind] ?? RadioIcon;
}

function gapBetween(a: string, b: string): number {
  const from = new Date(a).getTime();
  const to = new Date(b).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return to - from;
}

/** A detail value as one line. Objects are rare here and stringify acceptably. */
function render(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

/**
 * The per-order callback summary that sits under the timeline.
 *
 * Deliberately thin: the attempt history is already interleaved above, so this
 * exists to answer "was anything queued at all" and to hand off to the full
 * delivery screen rather than to duplicate it.
 */
export function OrderCallbacks({ orderId }: { orderId: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Callback deliveries</CardTitle>
        <CardDescription>
          Every attempt is on the timeline above. This opens the full delivery record.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Link
          to="/callbacks"
          search={{ order_id: orderId, status: "all" }}
          className="text-sm text-primary hover:underline"
        >
          Open this order's deliveries
        </Link>
      </CardContent>
    </Card>
  );
}
